import { useState, useEffect } from "react";
import {
  Button,
  Grid,
  CircularProgress,
  Checkbox,
  Autocomplete,
  TextField,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import InsertDriveFileRoundedIcon from "@mui/icons-material/InsertDriveFileRounded";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";

import {
  getDependencies,
  getLocations,
} from "../../services/LegalFileService";
import { getLawyers } from "../../services/LawyerService";
import { exportLiquidationSheet } from "../../services/LiquidationService";
import CardHeader from "../General/CardHeader";
import GlassCard from "../General/GlassCard";
import LoadingState from "../General/LoadingState";
import LegalFileStatusTaxFilter from "../General/LegalFileStatusTaxFilter";
import { useToast } from "../../context/ToastContext";

export default function ReportLiquidationSheet() {
  const { showToast } = useToast();
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const [dependencies, setDependencies] = useState([]);
  const [locations, setLocations] = useState([]);
  const [lawyers, setLawyers] = useState([]);

  const [selectedDependencies, setSelectedDependencies] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [selectedLawyers, setSelectedLawyers] = useState([]);
  const [selectedStatusTax, setSelectedStatusTax] = useState([]);

  const [loading, setLoading] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(true);

  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;

  useEffect(() => {
    async function fetchFilters() {
      setLoadingOptions(true);
      try {
        const [resDependencies, resLocations, resLawyers] = await Promise.all([
          getDependencies(),
          getLocations(),
          getLawyers(),
        ]);

        setDependencies(resDependencies.data.data || []);
        setLocations(resLocations.data.data || []);
        setLawyers(resLawyers.data.data || []);
      } catch (error) {
        console.error("Error loading filter options:", error);
        showToast("Error al cargar filtros.", "error");
      } finally {
        setLoadingOptions(false);
      }
    }

    fetchFilters();
  }, [showToast]);

  const handleGenerateReport = async () => {
    if (startDate && endDate && startDate.isAfter(endDate)) {
      showToast(
        "La fecha de inicio no puede ser posterior a la fecha de fin.",
        "error",
      );
      return;
    }

    setLoading(true);

    try {
      const payload = {
        dependencies: selectedDependencies,
        locations: selectedLocations,
        lawyers: selectedLawyers,
        status_tax_ids: selectedStatusTax.map((s) => s.id),
      };

      if (startDate) payload.start_date = startDate.format("YYYY-MM-DD");
      if (endDate) payload.end_date = endDate.format("YYYY-MM-DD");

      const response = await exportLiquidationSheet(payload);

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;

      const now = new Date();
      const formattedDate = now
        .toISOString()
        .slice(0, 19)
        .replace(/:/g, "-")
        .replace("T", "_");

      a.download = `planilla_liquidaciones_${formattedDate}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
      showToast("Ocurrió un error al generar la planilla.", "error");
    } finally {
      setLoading(false);
    }
  };

  if (loadingOptions) {
    return <LoadingState text="Cargando filtros..." />;
  }

  return (
    <Grid container justifyContent="center" sx={{ mt: 4 }}>
      <Grid size={{ xs: 12, sm: 12, md: 12, lg: 10 }}>
        <GlassCard>
          <CardHeader
            icon={<InsertDriveFileRoundedIcon />}
            title="Planilla de Liquidaciones"
            description="Expedientes con liquidación activa, con el desglose completo de rubros (capital, honorarios, gastos) y los totales."
          />

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 6 }}>
              <DatePicker
                label="Liquidación creada desde"
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                format="DD/MM/YYYY"
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 6 }}>
              <DatePicker
                label="Liquidación creada hasta"
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                format="DD/MM/YYYY"
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
              <Autocomplete
                multiple
                options={dependencies}
                disableCloseOnSelect
                getOptionLabel={(option) => option.name || option}
                value={selectedDependencies}
                onChange={(event, newValue) => setSelectedDependencies(newValue)}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox icon={icon} checkedIcon={checkedIcon} checked={selected} />
                    {option.name || option}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField {...params} label="Dependencias" placeholder="Seleccionar..." />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
              <Autocomplete
                multiple
                options={locations}
                disableCloseOnSelect
                getOptionLabel={(option) => option.name || option}
                value={selectedLocations}
                onChange={(event, newValue) => setSelectedLocations(newValue)}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox icon={icon} checkedIcon={checkedIcon} checked={selected} />
                    {option.name || option}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField {...params} label="Ubicaciones" placeholder="Seleccionar..." />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
              <Autocomplete
                multiple
                options={lawyers}
                disableCloseOnSelect
                getOptionLabel={(option) => `${option.name} ${option.lastname}` || option}
                value={selectedLawyers}
                onChange={(event, newValue) => setSelectedLawyers(newValue)}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox icon={icon} checkedIcon={checkedIcon} checked={selected} />
                    {`${option.name} ${option.lastname}` || option}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField {...params} label="Abogados" placeholder="Seleccionar..." />
                )}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
              <LegalFileStatusTaxFilter
                value={selectedStatusTax}
                onChange={setSelectedStatusTax}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={handleGenerateReport}
                disabled={loading}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Generar Planilla"
                )}
              </Button>
            </Grid>
          </Grid>
        </GlassCard>
      </Grid>
    </Grid>
  );
}
